import type {
    AddressProps,
    WeeklyHours,
} from '@myphx-engineering/phx-design-system';

import { getFragment } from '../../graphql/client/helpers/get-fragment';
import type {
    ChainProviderInfoFragment,
    MailOrderProviderInfoFragment,
    PharmacyProvider,
    PharmacyProviderInfoFragment,
    UnknownProviderInfoFragment,
} from '../../graphql/generated/graphql';
import type { MedicationConfigOverride } from '../../hooks/use-config-api';

import { mapWeeklyHours } from './map-weekly-hours';

export type ProcessedProvider = {
    id: string;
    name: string;
    phone?: string;
    url?: string;
    shippingTime?: string;
    deliveryDays?: string;
    address?: AddressProps['address'];
    weeklyHours?: WeeklyHours;
    logoUrl?: string;
    logoWidth?: number;
    chainName?: string;
    moreProviders?: PharmacyProviderInfoFragment[];
};

export type IncomingProviderFragment =
    | ChainProviderInfoFragment
    | PharmacyProviderInfoFragment
    | MailOrderProviderInfoFragment
    | UnknownProviderInfoFragment;

export const processProvidersToPharmacyInfo = (
    providers: IncomingProviderFragment[],
    configOverride: MedicationConfigOverride
): ProcessedProvider[] =>
    providers.map((p) => processProviderToPharmacyInfo(p, configOverride));

/**
 * Processes and extracts provider details into a standardized format for PharmacyInfo component.
 * @param provider - The provider to process and pass to PharmacyInfo.
 * @param configOverride - The medication config override from DrugProgramProvider context.
 * @returns The processed provider data to be passed to PharmacyInfo.
 */
export const processProviderToPharmacyInfo = (
    provider: IncomingProviderFragment,
    configOverride: MedicationConfigOverride
): ProcessedProvider => {
    const override =
        (provider.__typename === 'ChainProvider'
            ? configOverride.pharmacyOverrides[
                  (provider.pharmacies[0] as PharmacyProvider).id
              ]
            : configOverride.pharmacyOverrides[provider.id]) ?? provider;

    const props = {
        id: provider.id,
        name: override?.name,
        phone: override?.phone,
        url: override?.url ?? '',
        logoUrl: override.logoUrl,
        logoWidth: override.logoWidth,
        shippingTime: override.shippingTime,
        deliveryDays: override.deliveryDays,
    };

    switch (provider.__typename) {
        case 'ChainProvider': {
            const pharmacies = provider.pharmacies.map((p) => getFragment(p));
            const [primaryPharmacy, ...remainingPharmacies] = pharmacies;

            if (!primaryPharmacy) {
                return { ...props, name: override?.name ?? provider.chainName };
            }

            const weeklyHours = primaryPharmacy.weeklyHoursByDayOfWeek
                ? mapWeeklyHours(
                      getFragment(primaryPharmacy.weeklyHoursByDayOfWeek)
                  )
                : undefined;

            return {
                ...props,
                name: override?.name ?? provider.chainName,
                phone:
                    override?.phone ?? primaryPharmacy.phoneNumber ?? undefined,
                address: getFragment(primaryPharmacy.address),
                weeklyHours,
                chainName: provider.chainName,
                moreProviders: remainingPharmacies.length
                    ? remainingPharmacies
                    : undefined,
            };
        }
        case 'MailOrderProvider':
            return {
                ...props,
                name: override?.name ?? provider.name,
                phone: override?.phone ?? provider.phoneNumber ?? undefined,
            };
        case 'PharmacyProvider': {
            const weeklyHours = provider.weeklyHoursByDayOfWeek
                ? mapWeeklyHours(getFragment(provider.weeklyHoursByDayOfWeek))
                : undefined;

            return {
                ...props,
                name: override?.name ?? provider.name,
                phone: override?.phone ?? provider.phoneNumber ?? undefined,
                weeklyHours,
            };
        }
        default:
            return props;
    }
};
